'use client';

import { useEffect, useRef, useState } from 'react';
import { X, Check, Phone, Mail, Clock, AlertTriangle, FileText, Users, ArrowRight, Sparkles, Wrench } from 'lucide-react';
import { cn } from '@/lib/utils';

const ValueProposition = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const painPoints = [
    { icon: Phone, text: 'Endless phone calls with vendors' },
    { icon: Mail, text: 'Email chains that never end' },
    { icon: Clock, text: 'Emergency calls at 2am' },
    { icon: AlertTriangle, text: 'No visibility into what\'s happening' },
    { icon: FileText, text: 'Scattered documentation' },
    { icon: Users, text: 'Juggling multiple property types' },
  ];

  const solutions = [
    { text: 'Single point of contact for all facilities' },
    { text: 'Real-time dashboard for you AND your owners' },
    { text: '15-minute emergency response guarantee' },
    { text: 'Complete transparency at every step' },
    { text: 'Professional documentation and reporting' },
    { text: 'Universal platform for any property' },
  ];

  return (
    <section
      ref={sectionRef}
      className="relative py-24 lg:py-32 overflow-hidden"
    >
      {/* Full-width background gradient */}
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-primary-500/[0.02] to-transparent" />

      {/* Decorative side accents */}
      <div className="absolute top-0 left-0 w-32 h-full bg-gradient-to-r from-danger-500/[0.03] to-transparent" />
      <div className="absolute top-0 right-0 w-32 h-full bg-gradient-to-l from-success-500/[0.03] to-transparent" />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
        {/* Part 1: The Problem - Before/After Comparison */}
        <div className="mb-20">
          {/* Section header */}
          <div className="text-center mb-12">
            <span
              className={cn(
                "inline-block px-4 py-1.5 rounded-full text-xs font-semibold tracking-wider uppercase",
                "bg-danger-500/10 text-danger-400 border border-danger-500/20 mb-4",
                "transition-all duration-700",
                isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
              )}
            >
              The Problem
            </span>
            <h2
              className={cn(
                "font-playfair text-3xl md:text-4xl font-bold text-gray-900 dark:text-secondary-100 mb-4",
                "transition-all duration-700",
                isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
              )}
              style={{ transitionDelay: '100ms' }}
            >
              You Got Into Property Management
              <span className="text-gray-500 dark:text-secondary-400"> To Manage Properties</span>
            </h2>
            <p
              className={cn(
                "max-w-2xl mx-auto text-gray-500 dark:text-secondary-400",
                "transition-all duration-700",
                isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
              )}
              style={{ transitionDelay: '200ms' }}
            >
              Not to babysit vendors, chase contractors, or field emergency calls at all hours.
            </p>
          </div>

          {/* Before / After Grid - Horizontal layout */}
          <div className="grid md:grid-cols-2 gap-6 lg:gap-8">
            {/* Before Column */}
            <div
              className={cn(
                "relative p-6 rounded-2xl",
                "bg-gradient-to-br from-danger-500/5 to-transparent",
                "border border-danger-500/10",
                "transition-all duration-700",
                isVisible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-8"
              )}
              style={{ transitionDelay: '300ms' }}
            >
              <div className="absolute -top-3 left-6">
                <span className="px-3 py-1 bg-[#FAFBFC] dark:bg-[#0d0d0d] text-danger-400 text-xs font-semibold uppercase tracking-wide">
                  Without Axis Datum
                </span>
              </div>

              <div className="space-y-3 mt-4">
                {painPoints.map((point, i) => (
                  <div
                    key={i}
                    className={cn(
                      "flex items-center gap-3 p-2.5 rounded-lg",
                      "bg-danger-500/5 border border-danger-500/10",
                      "transition-all duration-500",
                      isVisible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-4"
                    )}
                    style={{ transitionDelay: `${400 + i * 50}ms` }}
                  >
                    <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-danger-500/10 flex items-center justify-center">
                      <point.icon className="w-4 h-4 text-danger-400" />
                    </div>
                    <span className="text-gray-600 dark:text-secondary-300 text-sm flex-1">{point.text}</span>
                    <X className="w-4 h-4 text-danger-500 flex-shrink-0" />
                  </div>
                ))}
              </div>
            </div>

            {/* After Column */}
            <div
              className={cn(
                "relative p-6 rounded-2xl",
                "bg-gradient-to-br from-success-500/5 to-transparent",
                "border border-success-500/10",
                "transition-all duration-700",
                isVisible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-8"
              )}
              style={{ transitionDelay: '300ms' }}
            >
              <div className="absolute -top-3 left-6">
                <span className="px-3 py-1 bg-[#FAFBFC] dark:bg-[#0d0d0d] text-success-400 text-xs font-semibold uppercase tracking-wide">
                  With Axis Datum
                </span>
              </div>

              <div className="space-y-3 mt-4">
                {solutions.map((point, i) => (
                  <div
                    key={i}
                    className={cn(
                      "flex items-center gap-3 p-2.5 rounded-lg",
                      "bg-success-500/5 border border-success-500/10",
                      "transition-all duration-500",
                      isVisible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-4"
                    )}
                    style={{ transitionDelay: `${400 + i * 50}ms` }}
                  >
                    <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-success-500/10 flex items-center justify-center">
                      <Check className="w-4 h-4 text-success-400" />
                    </div>
                    <span className="text-gray-700 dark:text-secondary-200 text-sm">{point.text}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Transition quote */}
          <div
            className={cn(
              "mt-10 text-center",
              "transition-all duration-700",
              isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
            )}
            style={{ transitionDelay: '800ms' }}
          >
            <p className="text-xl font-playfair text-gray-600 dark:text-secondary-300">
              "Stop reacting to chaos.
              <span className="text-primary-600 dark:text-primary-400"> Start operating with clarity.</span>"
            </p>
          </div>
        </div>

        {/* Visual Divider */}
        <div
          className={cn(
            "relative h-px mb-20",
            "transition-all duration-1000",
            isVisible ? "opacity-100" : "opacity-0"
          )}
          style={{ transitionDelay: '900ms' }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-primary-500/30 to-transparent" />
          <div className="absolute left-1/2 -translate-x-1/2 -translate-y-1/2 w-3 h-3 rounded-full bg-primary-500/50 blur-sm" />
          <div className="absolute left-1/2 -translate-x-1/2 -translate-y-1/2 w-2 h-2 rounded-full bg-primary-400" />
        </div>

        {/* Part 2: Choose Your Path */}
        <div id="features">
          {/* Section header */}
          <div className="text-center mb-12">
            <span
              className={cn(
                "inline-block px-4 py-1.5 rounded-full text-xs font-semibold tracking-wider uppercase",
                "bg-primary-500/10 text-primary-600 dark:text-primary-400 border border-primary-500/20 mb-4",
                "transition-all duration-700",
                isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
              )}
              style={{ transitionDelay: '950ms' }}
            >
              Choose Your Path
            </span>
            <h2
              className={cn(
                "font-playfair text-3xl md:text-4xl font-bold text-gray-900 dark:text-secondary-100 mb-4",
                "transition-all duration-700",
                isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
              )}
              style={{ transitionDelay: '1000ms' }}
            >
              Two Ways to Transform
              <span className="text-primary-600 dark:text-primary-400"> Your Operations</span>
            </h2>
          </div>

          {/* Two Cards - Side by side */}
          <div className="grid md:grid-cols-2 gap-6 lg:gap-8 items-stretch">
            {/* Self-Managed Card — Primary */}
            <div
              className={cn(
                "group relative",
                "transition-all duration-700",
                isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
              )}
              style={{ transitionDelay: '1100ms' }}
            >
              <div className="absolute -inset-0.5 bg-gradient-to-r from-primary-500/50 to-primary-400/50 rounded-2xl blur opacity-0 group-hover:opacity-30 transition-opacity duration-500" />

              <div className={cn(
                "relative h-full p-6 lg:p-8 rounded-2xl",
                "bg-gray-50 dark:bg-gradient-to-br dark:from-[#1a1a1a] dark:to-[#141414]",
                "border border-gray-200 dark:border-white/[0.08]",
                "hover:border-primary-500/30 transition-all duration-500"
              )}>
                <div className="absolute -top-3 right-6">
                  <span className="px-3 py-1 bg-primary-500 text-white text-xs font-bold rounded-full">
                    COMING Q3 2026
                  </span>
                </div>

                <div className="flex items-center gap-3 mb-5">
                  <div className="w-11 h-11 rounded-xl bg-primary-500/20 border border-primary-500/30 flex items-center justify-center">
                    <Wrench className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 dark:text-secondary-100">Axis Datum Platform</h3>
                    <p className="text-sm text-primary-600 dark:text-primary-400">Self-Managed Operations</p>
                  </div>
                </div>

                <p className="text-gray-500 dark:text-secondary-400 text-sm mb-5 leading-relaxed">
                  Take full control with our powerful platform. Manage your own work orders, track vendors,
                  and get the visibility you need — all with enterprise-grade tools at your fingertips.
                </p>

                <div className="space-y-2 mb-6">
                  {[
                    'Full Axis Datum platform access',
                    'Self-managed work orders',
                    'Vendor directory & coordination',
                    'Reporting & analytics dashboard',
                    'Mobile app access',
                    'Email & chat support',
                  ].map((feature, i) => (
                    <div key={i} className="flex items-center gap-2.5">
                      <Check className="w-4 h-4 text-primary-600 dark:text-primary-400 flex-shrink-0" />
                      <span className="text-sm text-gray-600 dark:text-secondary-300">{feature}</span>
                    </div>
                  ))}
                </div>

                <div className="pt-5 border-t border-gray-200 dark:border-white/[0.06]">
                  <div className="mb-1">
                    <span className="text-xs text-gray-400 dark:text-secondary-500">Starting at</span>
                  </div>
                  <div className="flex items-baseline gap-1 mb-1">
                    <span className="text-2xl font-bold text-gray-900 dark:text-secondary-100 font-playfair">$49</span>
                    <span className="text-gray-400 dark:text-secondary-500 text-sm">/mo</span>
                  </div>
                  <p className="text-xs text-gray-300 dark:text-secondary-600 mb-4">4 tiers from Starter to Business</p>

                  <a
                    href="#contact"
                    className={cn(
                      "group/btn inline-flex items-center justify-center gap-2 w-full py-2.5 px-5 rounded-xl",
                      "bg-primary-500 text-white font-semibold text-sm",
                      "hover:bg-primary-400 transition-all duration-300",
                      "shadow-lg dark:shadow-[0_0_30px_-8px_rgba(59,130,246,0.4)]"
                    )}
                  >
                    Join the Waitlist
                    <ArrowRight className="w-4 h-4 transition-transform group-hover/btn:translate-x-1" />
                  </a>
                </div>
              </div>
            </div>

            {/* Managed Service Card — CoreOps Cross-link */}
            <div
              className={cn(
                "group relative",
                "transition-all duration-700",
                isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
              )}
              style={{ transitionDelay: '1200ms' }}
            >
              <div className={cn(
                "relative h-full p-6 lg:p-8 rounded-2xl",
                "bg-white dark:bg-gradient-to-br dark:from-[#1a1a1a]/50 dark:to-[#141414]/50",
                "border border-gray-200 dark:border-white/[0.06]",
                "hover:border-gray-300 dark:hover:border-white/[0.12] transition-all duration-500"
              )}>
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-11 h-11 rounded-xl bg-white dark:bg-white/[0.05] border border-gray-200 dark:border-white/[0.08] flex items-center justify-center">
                    <Sparkles className="w-5 h-5 text-gray-500 dark:text-secondary-400" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 dark:text-secondary-100">Fully Managed</h3>
                    <p className="text-sm text-gray-500 dark:text-secondary-400">Powered by CoreOps</p>
                  </div>
                </div>

                <p className="text-gray-500 dark:text-secondary-400 text-sm mb-5 leading-relaxed">
                  Don't want to self-manage? CoreOps handles everything — dedicated coordinator,
                  complete vendor management, 24/7 emergency response, and full Axis Datum platform access included.
                </p>

                <div className="space-y-2 mb-6">
                  {[
                    'Dedicated account manager',
                    'Complete vendor coordination',
                    '24/7 emergency response',
                    'Full Axis Datum platform included',
                    'Monthly reporting & analytics',
                    'Unlimited work orders',
                  ].map((feature, i) => (
                    <div key={i} className="flex items-center gap-2.5">
                      <Check className="w-4 h-4 text-gray-400 dark:text-secondary-500 flex-shrink-0" />
                      <span className="text-sm text-gray-500 dark:text-secondary-400">{feature}</span>
                    </div>
                  ))}
                </div>

                <div className="pt-5 border-t border-gray-200 dark:border-white/[0.06]">
                  <div className="mb-1">
                    <span className="text-xs text-gray-400 dark:text-secondary-500">Starting at</span>
                  </div>
                  <div className="flex items-baseline gap-1 mb-1">
                    <span className="text-2xl font-bold text-gray-900 dark:text-secondary-100 font-playfair">$750</span>
                    <span className="text-gray-400 dark:text-secondary-500 text-sm">/mo</span>
                  </div>
                  <p className="text-xs text-gray-300 dark:text-secondary-600 mb-4">Scales with portfolio size</p>

                  <a
                    href="https://coreops-landing.vercel.app"
                    target="_blank"
                    rel="noopener noreferrer"
                    className={cn(
                      "group/btn inline-flex items-center justify-center gap-2 w-full py-2.5 px-5 rounded-xl",
                      "bg-transparent border border-gray-200 dark:border-white/[0.12] text-gray-700 dark:text-secondary-200 font-semibold text-sm",
                      "hover:bg-gray-50 dark:hover:bg-white/[0.04] hover:border-gray-300 dark:hover:border-white/20 transition-all duration-300"
                    )}
                  >
                    Learn About CoreOps
                    <ArrowRight className="w-4 h-4 transition-transform group-hover/btn:translate-x-1" />
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Bottom note */}
          <div
            className={cn(
              "mt-10 text-center",
              "transition-all duration-700",
              isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
            )}
            style={{ transitionDelay: '1300ms' }}
          >
            <p className="text-gray-400 dark:text-secondary-500 text-sm">
              Not sure which is right for you?{' '}
              <a href="#contact" className="text-primary-600 dark:text-primary-400 hover:text-primary-500 dark:hover:text-primary-300 transition-colors">
                Let's talk
              </a>
              {' '}— we'll help you find the perfect fit.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export { ValueProposition };
